import { useCallback, useReducer } from 'react';

import type { Notification } from 'entities/notification';

export type NotificationType = 'all' | 'urgent';

interface State {
  activeNotification: Notification | null;
  isOpen: boolean;
  notificationType: NotificationType;
}

type OpenUrgentNotificationsAction = {
  type: 'OPEN_URGENT_NOTIFICATIONS';
  payload: {
    notification: Notification;
  };
};

type OpenAllNotificationsAction = {
  type: 'OPEN_ALL_NOTIFICATIONS';
};

type CloseNotificationsAction = {
  type: 'CLOSE_NOTIFICATIONS';
};

type ResetActiveNotificationAction = {
  type: 'RESET_ACTIVE_NOTIFICATION';
};

type Action =
  | OpenUrgentNotificationsAction
  | OpenAllNotificationsAction
  | CloseNotificationsAction
  | ResetActiveNotificationAction;

const initialState: State = {
  activeNotification: null,
  isOpen: false,
  notificationType: 'all'
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'OPEN_URGENT_NOTIFICATIONS':
      return {
        ...state,
        isOpen: true,
        notificationType: 'urgent',
        activeNotification: action.payload.notification
      };
    case 'OPEN_ALL_NOTIFICATIONS':
      return { ...state, isOpen: true, notificationType: 'all' };
    case 'CLOSE_NOTIFICATIONS':
      return { ...state, isOpen: false };
    case 'RESET_ACTIVE_NOTIFICATION':
      return { ...state, activeNotification: null };
    default:
      return state;
  }
};

export const useNotificationsManager = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const openUrgentNotifications = useCallback(
    (notification: Notification) => {
      dispatch({ type: 'OPEN_URGENT_NOTIFICATIONS', payload: { notification } });
    },
    [dispatch]
  );

  const openAllNotifications = useCallback(() => {
    dispatch({ type: 'OPEN_ALL_NOTIFICATIONS' });
  }, [dispatch]);

  const closeNotifications = useCallback(() => {
    dispatch({ type: 'CLOSE_NOTIFICATIONS' });
  }, [dispatch]);

  const resetActiveNotification = useCallback(() => {
    dispatch({ type: 'RESET_ACTIVE_NOTIFICATION' });
  }, [dispatch]);

  return {
    activeNotification: state.activeNotification,
    isOpen: state.isOpen,
    notificationType: state.notificationType,
    openUrgentNotifications,
    openAllNotifications,
    closeNotifications,
    resetActiveNotification
  };
};
